/**
 * Copilot Service
 *
 * API client for the AI copilot chat functionality.
 * Handles conversation management and message sending.
 */

import { api } from './api.js';

const BASE_PATH = '/v1/copilot';

/**
 * List user's conversations
 * @param {Object} options - Query options
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.pageSize=20] - Items per page
 * @param {string} [options.status] - Filter by status
 * @returns {Promise<Object>} Paginated list of conversations
 */
export async function listConversations(options = {}) {
  const { page = 1, pageSize = 20, status } = options;

  const params = { page, pageSize };
  if (status) params.status = status;

  return api.get(`${BASE_PATH}/conversations`, params);
}

/**
 * Create a new conversation
 * @param {Object} data - Conversation data
 * @param {string} [data.title] - Optional title
 * @param {string} [data.initialMessage] - Optional first message
 * @returns {Promise<Object>} Created conversation
 */
export async function createConversation(data = {}) {
  return api.post(`${BASE_PATH}/conversations`, data);
}

/**
 * Get a specific conversation
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Conversation with messages
 */
export async function getConversation(conversationId) {
  return api.get(`${BASE_PATH}/conversations/${conversationId}`);
}

/**
 * Delete a conversation
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteConversation(conversationId) {
  return api.delete(`${BASE_PATH}/conversations/${conversationId}`);
}

/**
 * Send a message in a conversation
 * @param {string} conversationId - Conversation ID
 * @param {string} message - The message to send
 * @returns {Promise<Object>} Response with user and assistant messages
 */
export async function sendMessage(conversationId, message) {
  return api.post(`${BASE_PATH}/conversations/${conversationId}/messages`, {
    message,
  });
}

/**
 * Quick start a conversation with a message
 * Creates a new conversation and sends the initial message in one call.
 * @param {string} message - The initial message
 * @returns {Promise<Object>} Created conversation with first response
 */
export async function quickStart(message) {
  return api.post(`${BASE_PATH}/conversations`, {
    initialMessage: message,
  });
}

export default {
  listConversations,
  createConversation,
  getConversation,
  deleteConversation,
  sendMessage,
  quickStart,
};
